import { css } from 'styled-components';
import type { FlattenSimpleInterpolation } from 'styled-components';

/**
 * Convert HEX value to RGBA color.
 * Accepts 6-character HEX strings (with '#', e.g.: '#000000').
 * Based on https://stackoverflow.com/a/53936623
 *
 * @param {string} hex
 */
const _isValidHex = (hex: string) => /^#([A-Fa-f0-9]{3}){1,2}$/.test(hex);
const _getChunksFromString = (st: string, chunkSize: number) => st.match(new RegExp(`.{${chunkSize}}`, 'g'));
const _convertHexUnitTo256 = (hexStr: string) => parseInt(hexStr.repeat(2 / hexStr.length), 16);
const _getAlphafloat = (a: number, alpha: number) => {
  if (typeof a !== 'undefined') {
    return a / 255;
  }
  if (typeof alpha !== 'number' || alpha < 0 || alpha > 1) {
    return 1;
  }
  return alpha;
};

const hexToRGBA = (hex: string, alpha: number): string => {
  if (!_isValidHex(hex)) {
    throw new Error(`Invalid HEX: ${hex}`);
  }
  const chunkSize = Math.floor((hex.length - 1) / 3);
  const hexArr = _getChunksFromString(hex.slice(1), chunkSize);
  const [r, g, b, a] = hexArr?.map(_convertHexUnitTo256) || [];
  return `rgba(${r}, ${g}, ${b}, ${_getAlphafloat(a, alpha)})`;
};

// STYLED COMPONENTS MIXINS

const lineClamp = (size: number): FlattenSimpleInterpolation =>
  size
    ? css`
        display: -webkit-box;
        -webkit-line-clamp: ${size};
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
        overflow: hidden;
      `
    : css``;

const checkOverlap = (el1: Element | null, el2: Element | null): boolean => {
  if (!el1 || !el2 || getComputedStyle(el1).visibility === 'hidden' || getComputedStyle(el2).visibility === 'hidden') {
    return false;
  }

  const el1Rect = el1.getBoundingClientRect();
  const el2Rect = el2.getBoundingClientRect();

  const overlap = !(
    el1Rect.right < el2Rect.left ||
    el1Rect.left > el2Rect.right ||
    el1Rect.bottom < el2Rect.top ||
    el1Rect.top > el2Rect.bottom
  );

  return overlap;
};

export { checkOverlap, hexToRGBA as rgba, lineClamp };
